import { Button } from "@/components/base/button";
import {
  ActionMenuContent,
  ActionMenuItem,
  DropdownMenu,
  DropdownMenuTrigger,
} from "@/components/base/dropdown-menu";
import { TransparentInput } from "@/components/input";
import useUiStore from "@/store/ui.state";
import { LangKey, useTranslation } from "@vixi/localization";
import _ from "lodash";
import { forwardRef, useCallback, useMemo, useState } from "react";
import { useCourseEditorContext } from "../context";
import CourseTabButton, { PseudoTabButton } from "./TabButton";
import { TabItemProps, TabItemRenameProps } from "./TabItemProps";

// Assets
import Dots2VerticalIcon from "@assets/icons/dots-2-vertical.svg?react";

interface TextTabActionMenuProps extends Pick<TabItemRenameProps, "startRenaming" | "endRenaming"> {
  handleMove: (n: number) => void;
  deleteItem: () => void;
  duplicateItem: () => void;
}

export const TextTabActionMenu = ({
  deleteItem,
  duplicateItem,
  handleMove,
}: TextTabActionMenuProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const closeMenu = () => setOpen(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="text">
          <Dots2VerticalIcon style={{ height: "auto" }} />
        </Button>
      </DropdownMenuTrigger>

      <ActionMenuContent>
        <ActionMenuItem>
          <button
            onClick={() => {
              handleMove(-1);
              closeMenu();
            }}
          >
            {t("Action.MoveUp")}
          </button>
        </ActionMenuItem>

        <ActionMenuItem>
          <button
            onClick={() => {
              handleMove(1);
              closeMenu();
            }}
          >
            {t("Action.MoveDown")}
          </button>
        </ActionMenuItem>

        <ActionMenuItem>
          <button
            onClick={() => {
              duplicateItem();
              closeMenu();
            }}
          >
            {t("Action.Duplicate")}
          </button>
        </ActionMenuItem>

        <ActionMenuItem>
          <button
            onClick={() => {
              deleteItem();
              closeMenu();
            }}
          >
            {t("Action.Delete")}
          </button>
        </ActionMenuItem>
      </ActionMenuContent>
    </DropdownMenu>
  );
};

type TextTabItemProps = TabItemProps & TabItemRenameProps<HTMLInputElement>;

export const TextTabItem = forwardRef<HTMLButtonElement, TextTabItemProps>((props, ref) => {
  const { t, tNum, byLocale } = useTranslation();

  const {
    path,
    showNumber,
    text,
    isSelected,
    selectedTab,
    setSelectedTab,
    startRenaming,
    endRenaming,
    isRenaming,
    renameInputRef,
    newName,
    setNewName,
    onChange,
    tabNumber,
    disableDelete,
    singleTitle,
    ...otherProps
  } = props as TextTabItemProps;
  const { deleteItem, duplicateItem, moveBy } = useCourseEditorContext();
  const {
    showConfirmDialog,
    courseEditor: { mode },
  } = useUiStore();
  const isEditing = mode === "edit";

  const handleMove = useCallback(
    (n: number) => {
      const oldIndex = _.last(path) as number;
      const newIndex = moveBy(path, n);
      if (oldIndex === newIndex) return;

      const change = newIndex - oldIndex;

      if (isSelected) {
        setSelectedTab(selectedTab + change);
      }
    },
    [isSelected, moveBy, setSelectedTab, path],
  );

  const displayedText = useMemo(() => {
    const num = tabNumber !== undefined ? tNum(tabNumber) : "";
    const title = t(text as LangKey); // just in case the text is translatable

    return byLocale({
      fallback: `${num} ${title}`,
      ar: `${title} ${num}`,
    });
  }, [tabNumber, text]);

  const handleDelete = useCallback(
    (path: (string | number)[]) => {
      deleteItem(path);

      // If the selected tab is deleted, update the selected tab index
      if (isSelected) {
        const newIndex = Math.max(selectedTab - 1, 1);
        setSelectedTab(newIndex);
      }

      return true;
    },
    [deleteItem, isSelected, selectedTab, setSelectedTab],
  );

  const onDeleteClicked = useCallback(
    (path: (string | number)[]) => {
      showConfirmDialog({
        title: "Confirmation.DeleteTab.Title",
        description: `${t("Confirmation.DeleteTab.Description")} '${displayedText}'?`,
        onConfirm: () => handleDelete(path),
      });
    },
    [handleDelete, displayedText, showConfirmDialog, t],
  );

  return (
    <div className="relative flex items-center justify-between">
      {!isRenaming ? (
        <CourseTabButton
          ref={ref}
          showNumber={showNumber}
          variant="text"
          {...otherProps}
          children={displayedText}
        />
      ) : (
        <PseudoTabButton showNumber={showNumber} {...otherProps}>
          <TransparentInput
            ref={renameInputRef}
            className="px-1.5"
            value={newName}
            onBlur={() => endRenaming({ singleTitle: true })}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") endRenaming({ singleTitle: true });
              else if (e.key === "Escape") endRenaming({ cancel: true });
            }}
            onChange={(e) => setNewName(e.target.value)}
          />
        </PseudoTabButton>
      )}

      {
        <div className="opacity-0 transition-opacity group-hover/tab:opacity-100 group-has-data-[state=active]/tab:opacity-100 has-data-[state=open]:opacity-100">
          {isEditing && (
            <TextTabActionMenu
              startRenaming={startRenaming}
              endRenaming={() => endRenaming({ singleTitle: true })}
              deleteItem={() => onDeleteClicked(path)}
              duplicateItem={() => duplicateItem(path)}
              handleMove={handleMove}
            />
          )}
        </div>
      }
    </div>
  );
});

export default TextTabItem;
